// @ts-check
/**
 * Typed client for the UserPhoneNumber API — verified personal numbers
 * used as outbound caller IDs.
 *
 * Uses the global axios instance (baseURL, withCredentials, tenant_slug
 * interceptor all applied by main.js).
 */
import axios from 'axios'

const BASE = '/api/user-phone-numbers'

/** @typedef {import('./schema').components['schemas']} Schemas */
/** @typedef {Schemas['UserPhoneNumber']} UserPhoneNumber */
/** @typedef {Schemas['UserPhoneNumberCreate']} UserPhoneNumberCreate */
/** @typedef {Schemas['VerificationResponse']} VerificationResponse */

/**
 * GET /api/user-phone-numbers/ — list the current user's phone numbers.
 * @returns {Promise<UserPhoneNumber[]>}
 */
export async function listPhoneNumbers() {
  const { data } = await axios.get(`${BASE}/`)
  return data
}

/**
 * POST /api/user-phone-numbers/ — add a new phone number (E.164).
 * @param {UserPhoneNumberCreate} body
 * @returns {Promise<UserPhoneNumber>}
 */
export async function createPhoneNumber(body) {
  const { data } = await axios.post(`${BASE}/`, body)
  return data
}

/**
 * POST /api/user-phone-numbers/{id}/disable/ — disable a number (no deletion).
 * @param {number} id
 * @returns {Promise<UserPhoneNumber>}
 */
export async function disablePhoneNumber(id) {
  const { data } = await axios.post(`${BASE}/${id}/disable/`)
  return data
}

/**
 * POST /api/user-phone-numbers/{id}/request-verification/ — start Twilio validation.
 * Returns the validation code the user must enter when Twilio calls them.
 * @param {number} id
 * @returns {Promise<VerificationResponse>}
 */
export async function requestVerification(id) {
  const { data } = await axios.post(`${BASE}/${id}/request-verification/`)
  return data
}

/**
 * POST /api/user-phone-numbers/{id}/retry-verification/ — retry after failure.
 * @param {number} id
 * @returns {Promise<VerificationResponse>}
 */
export async function retryVerification(id) {
  const { data } = await axios.post(`${BASE}/${id}/retry-verification/`)
  return data
}
